// umlaut-check: skip — Datenschluessel ("produktlinie", "artikelnummer" usw.)
// sind technische Bezeichner aus der JSON-Antwort.

import React, {useEffect, useMemo, useState} from "react";
import {Link} from "@inertiajs/react";
import {Card, Container, OverlayTrigger, Spinner, Tooltip} from "react-bootstrap";
import axios from "axios";
import {FlexToast, useExternalControls} from "laravel-inertia-backend";
import {PiArrowLeftBold} from "react-icons/pi";
import {TOOLTIP_POPPER_CONFIG} from "../../Components/Cosmos/tooltipConfig.js";
import HeaderWithHelp from "../../Components/SalesAnalysis/HeaderWithHelp.jsx";
import PurposeBox from "../../Components/Cosmos/PurposeBox.jsx";
import Farbfeld from "../../Components/Cosmos/Farbfeld.jsx";
import AuslaufendAbzeichen from "../../Components/Cosmos/AuslaufendAbzeichen.jsx";
import ArtikelKennzahlKacheln from "../../Components/Cosmos/ArtikelKennzahlKacheln.jsx";
import ArtikelZeitstrahl from "../../Components/Cosmos/ArtikelZeitstrahl.jsx";
import ArtikelVerbrauchChart from "../../Components/Cosmos/ArtikelVerbrauchChart.jsx";
import ArtikelSaisonChart from "../../Components/Cosmos/ArtikelSaisonChart.jsx";
import ArtikelOskarVergleich from "../../Components/Cosmos/ArtikelOskarVergleich.jsx";
import ArtikelZulaeufeTabelle from "../../Components/Cosmos/ArtikelZulaeufeTabelle.jsx";
import ArtikelOffeneAuftraege from "../../Components/Cosmos/ArtikelOffeneAuftraege.jsx";
import ArtikelProduktionUndVerfuegbarkeit from "../../Components/Cosmos/ArtikelProduktionUndVerfuegbarkeit.jsx";
import ArtikelParameterKarte from "../../Components/Cosmos/ArtikelParameterKarte.jsx";
import AenderungsZeitleiste from "../../Components/Cosmos/AenderungsZeitleiste.jsx";
import useAnalysisData from "../../Components/SalesAnalysis/useAnalysisData.js";
import {hasAbility} from "../../Components/SalesAnalysis/format.js";
import {formatFarbnummer, produktlinieLabel} from "../../Components/Cosmos/format.js";

/** Referrer der Lieferfähigkeit (samt Filtern in der URL) als Ziel des
 * Zurück-Links - ohne einen fremden Baustein (LieferfaehigkeitTabelle.jsx)
 * für diesen Zweck anzufassen. Fällt auf die reine Einstiegsseite zurück,
 * wenn der Aufruf von woanders kam (Lesezeichen, neuer Tab). */
function zurueckZurLieferfaehigkeitHref() {
    if (typeof document === "undefined" || !document.referrer) {
        return "/cosmos/lieferfaehigkeit";
    }
    try {
        const referrer = new URL(document.referrer);
        if (referrer.origin === window.location.origin && referrer.pathname.startsWith("/cosmos/lieferfaehigkeit")) {
            return referrer.pathname + referrer.search;
        }
    } catch (e) {
        // Ungültiger Referrer - Rückfall auf die reine Einstiegsseite.
    }
    return "/cosmos/lieferfaehigkeit";
}

/**
 * Artikel-Blatt eines einzelnen Farbtons (/cosmos/artikel/{artikelnummer},
 * PLAN.md Abschnitt 5, Stufe 8 = AP8): Zeitstrahl mit den vier Marken,
 * Verbrauch zwei Jahre, Saisonfaktoren, Zuläufe, offene Kundenaufträge,
 * Änderungshistorie, Produktionsstand/Cosmos-Verfügbarkeit und die
 * Parameterpflege. Kein Menüpunkt (KONVENTIONEN.md Abschnitt 2).
 */
export default function CosmosArtikel({artikelnummer}) {
    const {addToast} = useExternalControls();
    const [angezeigt, setAngezeigt] = useState(null);
    const [aktualisiertGerade, setAktualisiertGerade] = useState(false);
    const {data, loading, error} = useAnalysisData(`/cosmos/daten/artikel/${artikelnummer}`, {});
    const zurueckHref = useMemo(() => zurueckZurLieferfaehigkeitHref(), []);

    // T11: "An dieser Stelle soll auch ein Aktualisieren-Button sein, um
    // diese offenen Kundenaufträge direkt zu aktualisieren" - holt nur die
    // offenen Aufträge DIESES Farbtons neu aus Weclapp (rein lesend Richtung
    // Weclapp) und tauscht das ganze Artikel-Blatt ohne Seiten-Neuladen aus.
    const offeneAuftraegeAktualisieren = () => {
        setAktualisiertGerade(true);
        axios
            .post(`/cosmos/daten/artikel/${artikelnummer}/offene-auftraege/aktualisieren`)
            .then(({data: antwort}) => {
                setAngezeigt(antwort);
                addToast(<FlexToast variant="success" title="Offene Kundenaufträge" message="Aktualisiert." />);
            })
            .catch((e) => {
                addToast(
                    <FlexToast
                        variant="danger"
                        title="Offene Kundenaufträge"
                        autohide={false}
                        message={e.response?.data?.message || "Aktualisieren fehlgeschlagen."}
                    />
                );
            })
            .finally(() => setAktualisiertGerade(false));
    };

    useEffect(() => {
        if (data) {
            setAngezeigt(data);
        }
    }, [data]);

    if (error) {
        return (
            <Container fluid className="pt-2 pb-4">
                <Link href={zurueckHref} className="small d-inline-flex align-items-center gap-1 mb-3">
                    <PiArrowLeftBold aria-hidden="true" /> Zurück zur Lieferfähigkeit
                </Link>
                <p className="text-danger">{error}</p>
            </Container>
        );
    }

    if (!angezeigt) {
        return (
            <Container fluid className="pt-2 pb-4">
                {loading && (
                    <div className="d-flex justify-content-center py-5">
                        <Spinner animation="border" role="status">
                            <span className="visually-hidden">Wird geladen…</span>
                        </Spinner>
                    </div>
                )}
            </Container>
        );
    }

    const {artikel, kennzahl, prognose, offene_kundenmenge: offeneKundenmenge, bedarfe, bestandshistorie, zulaeufe, offene_auftraege: offeneAuftraege, aenderungen, prognose_protokoll: prognoseProtokoll, produktionsstaende, verfuegbarkeiten, warenkoerbe, liefergelegenheiten, meta} = angezeigt;
    const canParameters = hasAbility(meta, "canParameters");

    return (
        <Container fluid className="pt-2 pb-4">
            <PurposeBox storageKey="artikel">
                Bündelt für einen einzelnen Farbton den Zeitstrahl mit den vier Marken (kritisch, leer, ideal,
                spät bestellen), Verbrauch und Bestandsverlauf, geplante Zuläufe und offene Kundenbestellungen
                sowie die drei Prognose-Szenarien. Dazu Änderungshistorie, gemessene und gepflegte
                Saisonfaktoren sowie die Parameter des Artikels.
            </PurposeBox>

            <Link href={zurueckHref} className="small d-inline-flex align-items-center gap-1 mb-2">
                <PiArrowLeftBold aria-hidden="true" /> Zurück zur Lieferfähigkeit
            </Link>

            <div className="d-flex align-items-center gap-2 mb-3 flex-wrap">
                <Farbfeld farbwert={artikel.farbwert} size={32} />
                <h1 className="h4 mb-0">
                    <HeaderWithHelp help="Alles zu diesem Farbton: Zeitstrahl, Verbrauch, Bestand, Prognose und Parameter.">
                        {artikel.farbnummer !== null ? `${formatFarbnummer(artikel.farbnummer, artikel.farbnummer_unter)} ` : ""}
                        {artikel.farbname || artikel.artikel_nummer}
                    </HeaderWithHelp>
                </h1>
                <span className="badge text-bg-light border">{produktlinieLabel(artikel.produktlinie)}</span>
                <span className="text-muted small">Art.-Nr. {artikel.artikel_nummer}</span>
                <span className={`badge ${artikel.aktiv ? "text-bg-success" : "text-bg-secondary"}`}>{artikel.aktiv ? "Aktiv" : "Inaktiv"}</span>
                {artikel.auslaufend && <AuslaufendAbzeichen />}
                {loading && <Spinner animation="border" size="sm" role="status" aria-label="Wird aktualisiert" />}
            </div>

            <ArtikelKennzahlKacheln prognose={prognose} offeneKundenmenge={offeneKundenmenge} paletteStueck={artikel.palette_stueck} />

            <Card className="shadow-sm mb-3">
                <Card.Header className="fw-semibold">
                    <HeaderWithHelp help="Prognostizierter Bestandsverlauf mit den vier Marken T kritisch/T leer/T ideal/T spät, den drei Szenarien und den geplanten Zuläufen.">
                        Zeitstrahl
                    </HeaderWithHelp>
                </Card.Header>
                <Card.Body>
                    <ArtikelZeitstrahl
                        prognose={prognose}
                        bestandshistorie={bestandshistorie}
                        zulaeufe={zulaeufe}
                        liefergelegenheiten={liefergelegenheiten}
                        prognoseProtokoll={prognoseProtokoll}
                    />
                </Card.Body>
            </Card>

            <div className="row g-3 mb-3">
                <div className="col-lg-8">
                    <ArtikelVerbrauchChart bedarfe={bedarfe} hinweise={kennzahl?.hinweise} />
                </div>
                <div className="col-lg-4">
                    <ArtikelSaisonChart kennzahl={kennzahl} artikel={artikel} />
                </div>
            </div>

            <ArtikelOskarVergleich oskarWerte={artikel.oskar_werte} prognose={prognose} />

            {zulaeufe.length > 0 && (
                <Card className="shadow-sm mb-3">
                    <Card.Header className="fw-semibold">
                        <HeaderWithHelp help="Geplante Lieferpositionen dieses Farbtons – KW Wunsch/Zusage/Ist, Mengen, Ampel und Status.">Zuläufe</HeaderWithHelp>
                    </Card.Header>
                    <Card.Body className="p-0">
                        <ArtikelZulaeufeTabelle zulaeufe={zulaeufe} />
                    </Card.Body>
                </Card>
            )}

            <Card className="shadow-sm mb-3">
                <Card.Header className="fw-semibold d-flex align-items-center justify-content-between">
                    <HeaderWithHelp help="Offene Kundenaufträge auf diesen Farbton (enge Definition: Status Auftrag wird erfasst / Auftragsbestätigung gedruckt).">
                        Offene Kundenaufträge
                    </HeaderWithHelp>
                    <OverlayTrigger
                        flip
                        popperConfig={TOOLTIP_POPPER_CONFIG}
                        placement="left"
                        overlay={
                            <Tooltip id="offene-auftraege-aktualisieren-tip">
                                Holt die offenen Aufträge dieses Farbtons neu aus Weclapp, dauert wenige Sekunden - ändert in
                                Weclapp nichts.
                            </Tooltip>
                        }
                    >
                        <span>
                            <button type="button" className="btn btn-sm btn-outline-secondary" disabled={aktualisiertGerade} onClick={offeneAuftraegeAktualisieren}>
                                {aktualisiertGerade ? "aktualisiert…" : "Aktualisieren"}
                            </button>
                        </span>
                    </OverlayTrigger>
                </Card.Header>
                {offeneAuftraege.length > 0 && (
                    <Card.Body className="p-0">
                        <ArtikelOffeneAuftraege auftraege={offeneAuftraege} />
                    </Card.Body>
                )}
            </Card>

            <div className="mb-3">
                <ArtikelProduktionUndVerfuegbarkeit produktionsstaende={produktionsstaende} verfuegbarkeiten={verfuegbarkeiten} warenkoerbe={warenkoerbe} />
            </div>

            {aenderungen.length > 0 && (
                <Card className="shadow-sm mb-3">
                    <Card.Header className="fw-semibold">
                        <HeaderWithHelp help="Wer wann was an diesem Artikel geändert hat, mit altem und neuem Wert.">Änderungshistorie</HeaderWithHelp>
                    </Card.Header>
                    <Card.Body>
                        <AenderungsZeitleiste aenderungen={aenderungen} />
                    </Card.Body>
                </Card>
            )}

            <ArtikelParameterKarte
                artikel={artikel}
                artikelnummer={artikelnummer}
                prognose={prognose}
                canParameters={canParameters}
                onGespeichert={(antwort) => setAngezeigt((a) => ({...a, artikel: antwort.artikel, prognose: antwort.prognose}))}
            />
        </Container>
    );
}
